import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet-async';
import {Box} from '@spothero/ui';
import useSearchViewParam from 'hooks/use-search-view-param';
import {useSelector} from 'react-redux';
import {SearchPageView} from 'utils/search-page-view';

/*
    ZONES
    This is a reusable, responsive layout component with 3 zones:
    - Zone 1/Primary view: Spot List and associated controls
    - Zone 2: Spot Map and associated controls
    - Zone 3: Spot Details Panel, Event Panel

    A button for toggling between list and map views (on mobile) is provided,
    but can be abstracted out depending on how the search experience evolves.

    Modals associated with search (e.g. event package details, destination info)
    could be considered as belonging to "Zone 4", but since they use portals,
    can be handled solely by the parent.

    STACKING
    The various zones and other pieces of the UI  have different display logic,
    depending on breakpoint and user interaction. From bottom to top:

    - Zone 2 (hidden initially on mobile)
    - Zone 1 (displayed initially on mobile; casts shadow on Zones 2+3 on desktop)
    - Mobile Toggle Button (appears above both Zones 1+2 on mobile, but below Zone 3; hidden on desktop)
    - Zone 3 (hidden initially. Appears above Zones 1+2 on mobile; attached to right of Zone 1 on desktop)
    - Global Navigation (appears over everything else except modals)
    - Modals (appear over everything)

    On desktop, Zone 3 can take up as much or as little space as possible above Zone 2.
    When the Event Panel is open, the map can still be moved underneath.
    However, when the Spot Details Panel is open, a blue overlay fills in the rest of the space
    and obscures the map.
*/

const ResponsiveSearchLayout = ({zone1, zone1OnScroll, zone2, zone3}) => {
    useSearchViewParam();
    const [baseZIndexLayer, setBaseZIndexLayer] = useState('layer1');
    const [top, setTop] = useState('0');
    const {view: searchPageView} = useSelector(state => state.search.data);

    useEffect(() => {
        if (searchPageView === SearchPageView.MOBILE_MAP) {
            setBaseZIndexLayer('layer3');
            setTop('150px');
        }
    }, [searchPageView]);

    return (
        <Box
            className="ResponsiveSearchLayout"
            position="absolute"
            top={{
                base: 'header-height-mobile',
                tablet: 'header-height-desktop',
            }}
            bottom="0"
            left="0"
            right="0"
        >
            <Helmet>
                <body className="responsive-search-page hide-footer" />
            </Helmet>
            <Box
                className="ResponsiveSearchLayout-zone-2"
                position="absolute"
                top={top}
                bottom="0"
                left={{base: '0', tablet: 'zone-1-width-desktop'}}
                right="0"
                zIndex={{
                    base: baseZIndexLayer,
                    tablet: 'layer1',
                }}
            >
                {zone2}
            </Box>
            <Box
                className="ResponsiveSearchLayout-zone-1"
                position="absolute"
                top="0"
                bottom="0"
                left="0"
                right="0"
                overflowY="scroll"
                width={{base: '100%', tablet: 'zone-1-width-desktop'}}
                backgroundColor="gray.light"
                boxShadow="md"
                zIndex="layer2"
                onScroll={zone1OnScroll}
            >
                {zone1}
            </Box>
            {Boolean(zone3) && (
                <Box
                    className="ResponsiveSearchLayout-zone-3"
                    position="absolute"
                    top="0"
                    bottom="0"
                    left={{base: '0', desktop: 'zone-1-width-desktop'}}
                    right="0"
                    zIndex={{base: 'layer3', desktop: 'layer1'}}
                >
                    {zone3}
                </Box>
            )}
        </Box>
    );
};

ResponsiveSearchLayout.propTypes = {
    zone1: PropTypes.node.isRequired,
    zone2: PropTypes.node.isRequired,
    zone3: PropTypes.node,
    zone1OnScroll: PropTypes.func,
};

export default ResponsiveSearchLayout;
